import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Button,
    CircularProgress,
    Table,
    TableHead,
    TableBody,
    TableCell,
    TableRow,
    TableContainer,
    Paper,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Snackbar,
    Menu,
    MenuItem,
    IconButton,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useNavigate } from "react-router-dom";
import { getAllTimeslotsAdmin, deleteTimeslot } from "../../Apis/TimeslotApi";

const GetAdminTimeslots = () => {
    const [timeslots, setTimeslots] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [openConfirm, setOpenConfirm] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    // Menu state
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedSlot, setSelectedSlot] = useState(null);

    const navigate = useNavigate();

    const fetchTimeslots = async () => {
        try {
            setLoading(true);
            const response = await getAllTimeslotsAdmin();
            setTimeslots(response?.timeSlots || []);
            setError(null);
        } catch (err) {
            console.error(err);
            setError("Failed to fetch timeslots.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTimeslots();
    }, []);

    const normalizeStatus = (val) =>
        val === true || val === "true" || val === 1 || val === "1";

    const handleMenuOpen = (event, slot) => {
        setAnchorEl(event.currentTarget);
        setSelectedSlot(slot);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedSlot(null);
    };

    const confirmDelete = (id) => {
        setSelectedId(id);
        setOpenConfirm(true);
        handleMenuClose();
    };

    const handleDelete = async () => {
        try {
            await deleteTimeslot(selectedId);
            setTimeslots((prev) =>
                prev.filter((slot) => (slot._id || slot.id) !== selectedId)
            );
            setSnackbar({
                open: true,
                message: "Timeslot deleted successfully!",
                severity: "success",
            });
        } catch (err) {
            console.error(err);
            setSnackbar({
                open: true,
                message: "Failed to delete timeslot",
                severity: "error",
            });
        } finally {
            setOpenConfirm(false);
            setSelectedId(null);
        }
    };

    const getStatusLabel = (status) => (normalizeStatus(status) ? "Active" : "Inactive");

    return (
        <Box sx={{ padding: 3, width: "90%" }}>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 3,
                }}
            >
                <Typography variant="h4" fontWeight="bold">
                    Timeslots
                </Typography>
                <Button variant="contained" onClick={() => navigate("/getadmintimeslot/add")}>
                    + Add Timeslot
                </Button>
            </Box>

            {loading && (
                <Box display="flex" justifyContent="center" alignItems="center" height="200px">
                    <CircularProgress />
                </Box>
            )}

            {error && (
                <Box sx={{ textAlign: "center", mb: 2 }}>
                    <MuiAlert severity="error">{error}</MuiAlert>
                </Box>
            )}

            {!loading && timeslots.length > 0 && (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>S.no</TableCell>
                                <TableCell>Time</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {timeslots.map((slot, index) => (
                                <TableRow key={slot._id || slot.id}>
                                    <TableCell>{index + 1}</TableCell>
                                    <TableCell>{slot.time}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={getStatusLabel(slot.status)}
                                            color={normalizeStatus(slot.status) ? "success" : "error"}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <IconButton onClick={(e) => handleMenuOpen(e, slot)}>
                                            <MoreVertIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {!loading && timeslots.length === 0 && !error && (
                <Typography sx={{ textAlign: "center", mt: 3 }}>No timeslots found.</Typography>
            )}

            {/* Menu for Actions */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
                <MenuItem
                    onClick={() => {
                        navigate("/getadmintimeslot/edit", {
                            state: { timeslotData: selectedSlot },
                        });
                        handleMenuClose();
                    }}
                >
                    Edit
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        confirmDelete(selectedSlot._id || selectedSlot.id);
                    }}
                >
                    Delete
                </MenuItem>
            </Menu>

            {/* Confirm Delete Dialog */}
            <Dialog open={openConfirm} onClose={() => setOpenConfirm(false)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this timeslot? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenConfirm(false)}>Cancel</Button>
                    <Button onClick={handleDelete} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar Notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
            >
                <MuiAlert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </MuiAlert>
            </Snackbar>
        </Box>
    );
};

export default GetAdminTimeslots;
